import { pushFaroEvent } from "@/services/faro.service";
import type { JSONContent } from "@tiptap/react";

export const EMPTY_DOC: JSONContent = { type: "doc", content: [] };

export function sanitizeTipTapContent(content: unknown): JSONContent {
  if (!isPlainObject(content)) {
    warnInvalid("non-object-input", content);
    return EMPTY_DOC;
  }

  const candidate = content as JSONContent;
  if (candidate.type !== "doc" || !Array.isArray(candidate.content)) {
    warnInvalid("not-a-valid-doc-node", content);
    return EMPTY_DOC;
  }

  const { children, droppedCount } = sanitizeChildList(candidate.content);

  if (droppedCount > 0) {
    warnInvalid("dropped-malformed-children", content, droppedCount);
  }

  return { ...candidate, content: children };
}

function warnInvalid(reason: string, content: unknown, droppedCount = 0): void {
  if (process.env.NODE_ENV === "test") return;
  pushFaroEvent("tiptap-content-sanitized", { reason, droppedCount });
  console.warn("[sanitizeTipTapContent]", reason, droppedCount, content);
}

interface SanitizedNode {
  node: JSONContent | null;
  droppedCount: number;
}

interface SanitizedChildList {
  children: JSONContent[];
  droppedCount: number;
}

function sanitizeChildList(contentList: readonly unknown[]): SanitizedChildList {
  const children: JSONContent[] = [];
  let droppedCount = 0;

  for (const child of contentList) {
    const { node, droppedCount: nestedDropped } = sanitizeNode(child);
    droppedCount += nestedDropped;
    if (node === null) {
      droppedCount++;
    } else {
      children.push(node);
    }
  }

  return { children, droppedCount };
}

function sanitizeNode(node: unknown): SanitizedNode {
  if (!isPlainObject(node)) return { node: null, droppedCount: 0 };
  const n = node as JSONContent;
  if (typeof n.type !== "string" || n.type.length === 0) return { node: null, droppedCount: 0 };

  if (Array.isArray(n.content)) {
    const { children, droppedCount } = sanitizeChildList(n.content);
    return { node: { ...n, content: children }, droppedCount };
  }

  return { node: n, droppedCount: 0 };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
